<script>
import {
  CFormFloating,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle
} from "@coreui/bootstrap-vue";

export default {
  name: "modalSalesOffer",
  components: {
    CFormSelect,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CFormFloating,
    CFormInput,
    CFormLabel,
  },
  props: {
    shown: Boolean
  },
  watch: {
    shown(value) {
      this.showModal = value;
      if (value) {
        this.loadRequiredProducts();
      }
    },
    showModal(value) {
      this.$emit('update:shown', value);
    }
  },
  mounted() {
    this.showModal = this.shown;
    if (this.shown) {
      this.loadRequiredProducts();
    }
  },
  methods: {
    async loadRequiredProducts() {
      if (this.$store.state.remember.length === 0 || this.loadingProducts) {
        return;
      }

      this.loadingProducts = true;
      this.productLoadError = '';

      try {
        const response = await this.axios.post('/offer/products', {
          recipe_ids: this.$store.state.remember.map(recipe => recipe.id),
        });
        const previousDiscounts = Object.fromEntries(
          this.requiredProducts.map(product => [product.article_number, product.discount])
        );
        this.requiredProducts = response.data.map(product => ({
          ...product,
          discount: previousDiscounts[product.article_number] ?? '',
        }));
      } catch (error) {
        this.requiredProducts = [];
        this.productLoadError = error.response?.data?.message || 'Die benötigten Produkte konnten nicht ermittelt werden.';
      } finally {
        this.loadingProducts = false;
      }
    },
    async createOffer() {
      if (this.loading || this.$store.state.remember.length === 0) {
        return;
      }

      this.loading = true;

      const articleDiscounts = {};
      this.requiredProducts.forEach(product => {
        if (product.discount !== '' && product.discount !== null) {
          articleDiscounts[product.article_number] = Number(product.discount);
        }
      });

      try {
        const response = await this.axios({
          url: "/offer",
          method: 'POST',
          responseType: 'blob',
          data: {
            recipe_ids: this.$store.state.remember.map(recipe => recipe.id),
            desired_litres: this.formData.calculationType === 'litres'
              ? Number(this.formData.desiredLitres)
              : null,
            target_net: this.formData.calculationType === 'budget'
              ? Number(this.formData.targetNet)
              : null,
            default_discount: Number(this.formData.defaultDiscount),
            article_discounts: articleDiscounts,
            selling_price_per_scoop: Number(this.formData.sellingPricePerScoop),
            scoops_per_litre: Number(this.formData.scoopsPerLitre),
            include_economics: this.formData.includeEconomics,
            include_shipping_methods: this.formData.includeShippingMethods,
            customer: this.formData.customer,
          },
        });

        const fileURL = window.URL.createObjectURL(response.data);
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', 'Angebot.pdf');
        document.body.appendChild(fileLink);
        fileLink.click();
        fileLink.remove();
        window.URL.revokeObjectURL(fileURL);
        this.showModal = false;
      } catch (error) {
        let message = 'Das Angebot konnte nicht erstellt werden.';

        if (error.response && error.response.data instanceof Blob) {
          const responseText = await error.response.data.text();
          try {
            const responseData = JSON.parse(responseText);
            message = responseData.message || Object.values(responseData.errors || {}).flat().join('<br>') || message;
          } catch (parseError) {
            message = responseText || message;
          }
        }

        this.$toast.error(message);
      } finally {
        this.loading = false;
      }
    },
  },
  data() {
    return {
      showModal: false,
      loading: false,
      loadingProducts: false,
      productLoadError: '',
      requiredProducts: [],
      formData: {
        calculationType: 'litres',
        desiredLitres: 10,
        targetNet: 2500,
        defaultDiscount: 0,
        sellingPricePerScoop: 2,
        scoopsPerLitre: 16,
        includeEconomics: true,
        includeShippingMethods: true,
        customer: {
          company: '',
          name: '',
          street: '',
          postal_code: '',
          city: '',
          salutation: '',
        },
      },
    }
  }
}
</script>

<template>
  <CModal class="creator-modal-above-offcanvas" :visible="showModal" alignment="top" size="xl" @close="showModal=false">
    <CModalHeader>
      <CModalTitle>Neues Rohstoffangebot erstellen</CModalTitle>
    </CModalHeader>
    <CModalBody>
      <div class="mb-4">
        <h6>Ausgewählte Rezepte ({{ $store.state.remember.length }})</h6>
        <span
          v-for="recipe in $store.state.remember"
          :key="recipe.id"
          class="badge bg-secondary me-1 mb-1"
        >{{ recipe.title || 'Unbenannt' }}</span>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6">
          <h6>Berechnung</h6>
          <CFormFloating class="mb-3">
            <CFormSelect id="offer-calculation-type" v-model="formData.calculationType">
              <option value="litres">Feste Wunschliterzahl je Rezept</option>
              <option value="budget">Maximale gemeinsame Literzahl für Netto-Budget</option>
            </CFormSelect>
            <CFormLabel for="offer-calculation-type">Berechnungsart</CFormLabel>
          </CFormFloating>

          <CFormFloating v-if="formData.calculationType === 'litres'" class="mb-3">
            <CFormInput
              id="offer-desired-litres"
              v-model.number="formData.desiredLitres"
              type="number"
              min="0.01"
              step="0.01"
            />
            <CFormLabel for="offer-desired-litres">Wunschliter je Rezept</CFormLabel>
          </CFormFloating>

          <CFormFloating v-else class="mb-3">
            <CFormInput
              id="offer-target-net"
              v-model.number="formData.targetNet"
              type="number"
              min="0.01"
              step="0.01"
            />
            <CFormLabel for="offer-target-net">Netto-Budget in €</CFormLabel>
          </CFormFloating>

          <CFormFloating class="mb-3">
            <CFormInput
              id="offer-default-discount"
              v-model.number="formData.defaultDiscount"
              type="number"
              min="0"
              max="100"
              step="0.01"
            />
            <CFormLabel for="offer-default-discount">Standardrabatt in %</CFormLabel>
          </CFormFloating>

          <div class="form-check mb-3">
            <input
              id="offer-include-economics"
              v-model="formData.includeEconomics"
              class="form-check-input"
              type="checkbox"
            >
            <label class="form-check-label" for="offer-include-economics">
              Wirtschaftlichkeitsberechnung im Angebot anzeigen
            </label>
          </div>

          <div class="form-check mb-3">
            <input
              id="offer-include-shipping-methods"
              v-model="formData.includeShippingMethods"
              class="form-check-input"
              type="checkbox"
            >
            <label class="form-check-label" for="offer-include-shipping-methods">
              Zahlung und Lieferung hinzufügen
            </label>
          </div>

          <div v-if="formData.includeEconomics" class="row">
            <div class="col-sm-6">
              <CFormFloating class="mb-3">
                <CFormInput
                  id="offer-price-per-scoop"
                  v-model.number="formData.sellingPricePerScoop"
                  type="number"
                  min="0.01"
                  step="0.01"
                />
                <CFormLabel for="offer-price-per-scoop">Verkaufspreis je Kugel in €</CFormLabel>
              </CFormFloating>
            </div>
            <div class="col-sm-6">
              <CFormFloating class="mb-3">
                <CFormInput
                  id="offer-scoops-per-litre"
                  v-model.number="formData.scoopsPerLitre"
                  type="number"
                  min="1"
                  step="1"
                />
                <CFormLabel for="offer-scoops-per-litre">Kugeln je Liter</CFormLabel>
              </CFormFloating>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <h6>Kundendaten</h6>
          <CFormFloating class="mb-3">
            <CFormInput id="offer-company" v-model="formData.customer.company" type="text" />
            <CFormLabel for="offer-company">Firma</CFormLabel>
          </CFormFloating>
          <CFormFloating class="mb-3">
            <CFormInput id="offer-customer-name" v-model="formData.customer.name" type="text" />
            <CFormLabel for="offer-customer-name">Ansprechpartner</CFormLabel>
          </CFormFloating>
          <CFormFloating class="mb-3">
            <CFormInput id="offer-street" v-model="formData.customer.street" type="text" />
            <CFormLabel for="offer-street">Straße und Hausnummer</CFormLabel>
          </CFormFloating>
          <div class="row">
            <div class="col-sm-4">
              <CFormFloating class="mb-3">
                <CFormInput id="offer-postal-code" v-model="formData.customer.postal_code" type="text" />
                <CFormLabel for="offer-postal-code">PLZ</CFormLabel>
              </CFormFloating>
            </div>
            <div class="col-sm-8">
              <CFormFloating class="mb-3">
                <CFormInput id="offer-city" v-model="formData.customer.city" type="text" />
                <CFormLabel for="offer-city">Ort</CFormLabel>
              </CFormFloating>
            </div>
          </div>
          <CFormFloating class="mb-3">
            <CFormInput id="offer-salutation" v-model="formData.customer.salutation" type="text" />
            <CFormLabel for="offer-salutation">Anrede im Angebot</CFormLabel>
          </CFormFloating>
        </div>
      </div>

      <hr>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div>
          <h6 class="mb-0">Benötigte Produkte und Artikelrabatte</h6>
          <small class="text-muted">Ein eingetragener Produktrabatt überschreibt den Standardrabatt.</small>
        </div>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="loadingProducts"
          @click="loadRequiredProducts"
        >
          <span v-if="loadingProducts" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <i v-else class="fa-solid fa-rotate"></i> Produkte neu ermitteln
        </button>
      </div>

      <div v-if="loadingProducts" class="text-center text-muted py-4">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Benötigte Produkte werden ermittelt …
      </div>
      <div v-else-if="productLoadError" class="alert alert-danger mb-0">
        {{ productLoadError }}
      </div>
      <div v-else-if="requiredProducts.length === 0" class="alert alert-warning mb-0">
        Für die ausgewählten Rezepte wurden keine bestellbaren Produkte gefunden.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>Produkt</th>
              <th>Artikelnummer</th>
              <th class="text-end">Gebinde</th>
              <th class="text-end">Menge bei 1 Liter je Rezept</th>
              <th style="width: 180px">Produktrabatt</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in requiredProducts" :key="product.article_number">
              <td>
                <div>{{ product.title }}</div>
                <small v-if="product.description" class="text-muted">{{ product.description }}</small>
              </td>
              <td>{{ product.article_number }}</td>
              <td class="text-end">{{ product.unit_quantity }} {{ product.unit }}</td>
              <td class="text-end">{{ product.quantity }} × Gebinde</td>
              <td>
                <div class="input-group input-group-sm">
                  <input
                    :id="`offer-article-discount-${index}`"
                    v-model.number="product.discount"
                    class="form-control"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    :placeholder="`${formData.defaultDiscount}`"
                    :aria-label="`Rabatt für ${product.title}`"
                  >
                  <span class="input-group-text">%</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </CModalBody>
    <CModalFooter class="text-end">
      <button class="btn btn-outline-secondary btn-sm" @click="showModal=false" :disabled="loading">Abbrechen</button>
      <button
        class="btn btn-primary btn-sm"
        :disabled="loading || loadingProducts || requiredProducts.length === 0"
        @click="createOffer"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        Angebot erstellen und herunterladen
      </button>
    </CModalFooter>
  </CModal>
</template>

<style>
.creator-modal-above-offcanvas.modal {
  z-index: 10000001 !important;
}

.modal-backdrop {
  z-index: 10000000 !important;
}
</style>
